/**
 * Queue Domain Utils
 */

import type {
  LoadingLocation,
  LoadingLocationFilter,
  LoadingLocationStats,
  QueueEntry,
  QueueEntryFilter,
  QueueStatus,
  LocationKind,
} from '../types'
import {
  QUEUE_STATUS_CONFIG,
  QUEUE_DISPLAY_STATUS_CONFIG,
  ACTIVE_QUEUE_STATUSES,
  type QueueDisplayStatusType,
} from '../constants'

// ============================================================================
// Loading Location Filters
// ============================================================================

export function filterLocationsByKind(
  locations: LoadingLocation[],
  kind: LocationKind | 'all'
): LoadingLocation[] {
  if (kind === 'all') return locations
  return locations.filter((loc) => loc.locationKind === kind)
}

export function filterLocationsByActive(
  locations: LoadingLocation[],
  status: 'all' | 'active' | 'inactive'
): LoadingLocation[] {
  if (status === 'all') return locations
  return locations.filter((loc) =>
    status === 'active' ? loc.isActive : !loc.isActive
  )
}

export function filterLocationsBySearch(
  locations: LoadingLocation[],
  search: string
): LoadingLocation[] {
  if (!search.trim()) return locations
  const term = search.toLowerCase()
  return locations.filter(
    (loc) =>
      loc.label.toLowerCase().includes(term) ||
      loc.variant?.name?.toLowerCase().includes(term) ||
      loc.allowedVariants?.some(
        (variant) =>
          variant.name.toLowerCase().includes(term) ||
          variant.variantCode.toLowerCase().includes(term)
      )
  )
}

export function filterLocationsByFactory(
  locations: LoadingLocation[],
  factoryId?: string
): LoadingLocation[] {
  if (!factoryId) return locations
  return locations.filter((loc) => loc.factory?.documentId === factoryId)
}

export function filterLocationsByVariant(
  locations: LoadingLocation[],
  variantId?: string
): LoadingLocation[] {
  if (!variantId) return locations
  return locations.filter((loc) => locationMatchesVariant(loc, variantId))
}

export function locationMatchesVariant(
  location: LoadingLocation,
  variantId: string
): boolean {
  const normalizedVariantId = String(variantId)

  if (location.variant) {
    if (
      location.variant.documentId === normalizedVariantId ||
      location.variant.id === normalizedVariantId
    ) {
      return true
    }
  }

  return (
    location.allowedVariants?.some(
      (variant) =>
        variant.documentId === normalizedVariantId || variant.id === normalizedVariantId
    ) ?? false
  )
}

export function applyLocationFilters(
  locations: LoadingLocation[],
  filter: LoadingLocationFilter
): LoadingLocation[] {
  let result = locations
  result = filterLocationsByKind(result, filter.locationKind)
  result = filterLocationsByActive(result, filter.isActive)
  result = filterLocationsBySearch(result, filter.search)
  if (filter.factoryId) {
    result = filterLocationsByFactory(result, filter.factoryId)
  }
  if (filter.variantId) {
    result = filterLocationsByVariant(result, filter.variantId)
  }
  return result
}

export function calculateLocationStats(
  locations: LoadingLocation[]
): LoadingLocationStats {
  return {
    total: locations.length,
    active: locations.filter((loc) => loc.isActive).length,
    bays: locations.filter((loc) => loc.locationKind === 'bay').length,
    silos: locations.filter((loc) => loc.locationKind === 'silo').length,
    totalCapacity: locations.reduce((sum, loc) => sum + loc.maxVehicles, 0),
    totalOccupancy: locations.reduce(
      (sum, loc) => sum + (loc.currentOccupancy || 0),
      0
    ),
  }
}

// ============================================================================
// Queue Entry Filters
// ============================================================================

export function filterEntriesByStatus(
  entries: QueueEntry[],
  status: QueueStatus | 'all' | 'active'
): QueueEntry[] {
  if (status === 'all') return entries
  if (status === 'active') {
    return entries.filter((entry) =>
      ACTIVE_QUEUE_STATUSES.includes(entry.queueStatus as any)
    )
  }
  return entries.filter((entry) => entry.queueStatus === status)
}

export function filterEntriesBySearch(
  entries: QueueEntry[],
  search: string
): QueueEntry[] {
  if (!search.trim()) return entries
  const term = search.toLowerCase()
  return entries.filter(
    (entry) =>
      entry.displayNumber.toLowerCase().includes(term) ||
      entry.driverName?.toLowerCase().includes(term) ||
      entry.vehiclePlate?.toLowerCase().includes(term) ||
      entry.driverPhone?.includes(term) ||
      entry.order?.orderNumber?.toLowerCase().includes(term)
  )
}

export function filterEntriesByLocation(
  entries: QueueEntry[],
  locationId?: string
): QueueEntry[] {
  if (!locationId) return entries
  return entries.filter(
    (entry) => entry.loadingLocation?.documentId === locationId
  )
}

export function applyEntryFilters(
  entries: QueueEntry[],
  filter: QueueEntryFilter
): QueueEntry[] {
  let result = entries
  result = filterEntriesByStatus(result, filter.status)
  result = filterEntriesBySearch(result, filter.search)
  if (filter.loadingLocationId) {
    result = filterEntriesByLocation(result, filter.loadingLocationId)
  }
  return result
}

// ============================================================================
// Queue Status Helpers
// ============================================================================

export function getStatusConfig(status: QueueStatus) {
  return QUEUE_STATUS_CONFIG[status] || QUEUE_STATUS_CONFIG.waiting
}

/**
 * Status to show on screen. A truck with a loading completion time is done
 * loading and only needs an exit, so show 'loaded' instead of 'entered' /
 * 'loading'. Display only — never send this back to the API.
 *
 * Terminal statuses (completed, cancelled, expired, no_show) win: a cancelled
 * entry that happens to carry a loadingCompletedAt is still cancelled.
 */
export function getQueueDisplayStatus(
  entry: Pick<QueueEntry, 'queueStatus' | 'loadingCompletedAt'>,
): QueueDisplayStatusType {
  if (!entry.loadingCompletedAt) return entry.queueStatus
  if (entry.queueStatus !== 'entered' && entry.queueStatus !== 'loading') {
    return entry.queueStatus
  }
  return 'loaded'
}

export function getDisplayStatusConfig(status: QueueDisplayStatusType) {
  return QUEUE_DISPLAY_STATUS_CONFIG[status] || QUEUE_DISPLAY_STATUS_CONFIG.waiting
}

export function isActiveStatus(status: QueueStatus): boolean {
  return ACTIVE_QUEUE_STATUSES.includes(status as any)
}

export function canCancelEntry(status: QueueStatus): boolean {
  return getStatusConfig(status).canCancel
}

export function canEnterFactory(status: QueueStatus): boolean {
  return getStatusConfig(status).canEnter
}

// ============================================================================
// Wait Time Helpers
// ============================================================================

export function formatWaitTime(minutes: number): string {
  if (minutes < 60) {
    return `${minutes} min`
  }
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  if (mins === 0) {
    return `${hours}h`
  }
  return `${hours}h ${mins}m`
}

export function calculateEstimatedWait(
  position: number,
  avgLoadingTime: number,
  capacity: number
): number {
  if (!position || !avgLoadingTime || !capacity) return 0
  return Math.ceil((position / capacity) * avgLoadingTime)
}

// ============================================================================
// Display Formatters
// ============================================================================

export function formatDisplayNumber(
  locationKind: LocationKind,
  locationNumber: number,
  queueNumber: number
): string {
  const kindPrefix = locationKind === 'bay' ? 'BAY' : 'SILO'
  const paddedQueue = String(queueNumber).padStart(3, '0')
  return `${kindPrefix}-${locationNumber}-${paddedQueue}`
}

export function formatCapacity(current: number, max: number): string {
  return `${current}/${max}`
}

export function getOccupancyPercentage(current: number, max: number): number {
  if (max === 0) return 0
  return Math.round((current / max) * 100)
}

export function getOccupancyLevel(
  current: number,
  max: number
): 'low' | 'medium' | 'high' | 'full' {
  const percentage = getOccupancyPercentage(current, max)
  if (percentage >= 100) return 'full'
  if (percentage >= 75) return 'high'
  if (percentage >= 50) return 'medium'
  return 'low'
}

export const OCCUPANCY_COLORS = {
  low: 'bg-green-500',
  medium: 'bg-yellow-500',
  high: 'bg-orange-500',
  full: 'bg-red-500',
} as const

// ============================================================================
// Sorting Helpers
// ============================================================================

export function sortLocationsByPriority(
  locations: LoadingLocation[]
): LoadingLocation[] {
  return [...locations].sort((a, b) => (b.priority || 0) - (a.priority || 0))
}

export function sortEntriesByQueueNumber(entries: QueueEntry[]): QueueEntry[] {
  return [...entries].sort((a, b) => a.queueNumber - b.queueNumber)
}

export function sortEntriesByRequestedAt(entries: QueueEntry[]): QueueEntry[] {
  return [...entries].sort(
    (a, b) =>
      new Date(a.requestedAt).getTime() - new Date(b.requestedAt).getTime()
  )
}

// ============================================================================
// Additional Helpers needed by presentation layer
// ============================================================================

export function getQueueStatusInfo(status: QueueStatus) {
  const config = QUEUE_STATUS_CONFIG[status] || QUEUE_STATUS_CONFIG.waiting
  return {
    ...config,
    status,
  }
}

export function isEntryActive(entry: QueueEntry): boolean {
  return isActiveStatus(entry.queueStatus)
}

export function formatTimeAgo(dateStr: string, locale: string = 'en'): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / (1000 * 60))
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  try {
    const rtf = new Intl.RelativeTimeFormat(locale === 'ar' ? 'ar-SA' : 'en', { numeric: 'auto' })
    if (diffMins < 1) return rtf.format(0, 'minute')
    if (diffMins < 60) return rtf.format(-diffMins, 'minute')
    if (diffHours < 24) return rtf.format(-diffHours, 'hour')
    return rtf.format(-diffDays, 'day')
  } catch {
    // Fallback for environments without Intl.RelativeTimeFormat
    if (diffMins < 1) return 'just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    return `${diffDays}d ago`
  }
}

export function formatDateTime(dateStr: string, locale: string = 'en'): string {
  const date = new Date(dateStr)
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }
  return date.toLocaleString(locale === 'ar' ? 'ar-SA' : 'en-US', options)
}

export function formatDuration(ms: number, locale: string = 'en'): string {
  const minutes = Math.floor(ms / (1000 * 60))
  const hours = Math.floor(minutes / 60)
  const remainingMins = minutes % 60

  if (locale === 'ar') {
    if (hours > 0) {
      return `${hours} ساعة ${remainingMins > 0 ? `و ${remainingMins} دقيقة` : ''}`
    }
    return `${minutes} دقيقة`
  }

  if (hours > 0) {
    return `${hours}h ${remainingMins > 0 ? `${remainingMins}m` : ''}`
  }
  return `${minutes}m`
}

export function calculateWaitTime(entry: QueueEntry): number | null {
  if (!entry.requestedAt) return null
  
  const start = new Date(entry.requestedAt)
  const end = entry.notifiedAt 
    ? new Date(entry.notifiedAt) 
    : entry.enteredFactoryAt 
      ? new Date(entry.enteredFactoryAt)
      : new Date()
  
  return end.getTime() - start.getTime()
}

export function generateDisplayNumber(
  locationKind: LocationKind,
  locationNumber: number,
  queueNumber: number
): string {
  return formatDisplayNumber(locationKind, locationNumber, queueNumber)
}

// ============================================================================
// Plant side (outside / inside) — what the security guard works from
// ============================================================================

/** Still queuing at the gate. Its clock runs from the gate scan. */
export const OUTSIDE_PLANT_STATUSES: QueueStatus[] = ['waiting', 'ready', 'called']
/** Admitted. Its clock runs from the moment it entered. */
export const INSIDE_PLANT_STATUSES: QueueStatus[] = ['entered', 'loading']

/**
 * Split active entries into outside/inside the plant, longest-standing first —
 * the truck that has been there longest is the one somebody has to act on.
 *
 * Outside is timed from `requestedAt` (the gate scan, the same instant the
 * backend measures waiting time from); inside from `enteredFactoryAt`, falling
 * back to the scan when that was never recorded — a too-long clock beats a
 * truck reading 0m forever.
 *
 * `now` is a parameter so this stays pure and testable; callers pass Date.now().
 */
export function splitTrucksByPlantSide(
  entries: QueueEntry[],
  now: number
): { outside: QueueEntry[]; inside: QueueEntry[] } {
  return {
    outside: sortByElapsed(entries, OUTSIDE_PLANT_STATUSES, 'requestedAt', now),
    inside: sortByElapsed(entries, INSIDE_PLANT_STATUSES, 'enteredFactoryAt', now),
  }
}

function sortByElapsed(
  entries: QueueEntry[],
  statuses: QueueStatus[],
  clockField: 'requestedAt' | 'enteredFactoryAt',
  now: number
): QueueEntry[] {
  return entries
    .filter((e) => statuses.includes(e.queueStatus))
    .map((e) => ({ entry: e, elapsed: elapsedSince(e[clockField] ?? e.requestedAt, now) }))
    .sort((a, b) => b.elapsed - a.elapsed)
    .map((row) => row.entry)
}

/** Floored at 0: a clock skew must not render as a negative wait. */
export function elapsedSince(iso: string | undefined, now: number): number {
  if (!iso) return 0
  const started = new Date(iso).getTime()
  if (Number.isNaN(started)) return 0
  return Math.max(0, now - started)
}

/** Milliseconds as "45m" / "3h 39m". */
export function formatElapsed(ms: number): string {
  const mins = Math.floor(ms / 60_000)
  if (mins < 60) return `${mins}m`
  return `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, '0')}m`
}

// Queue simulation — reading a simulated run
export * from './simulation.utils'

// Kiosk wait times — how urgent a wait looks
export * from './wait-level'
export * from './wait-groups'
